import { LibraryDB } from './LibraryDB';

const BASE_URL = 'https://gutendex.com/books/';

export interface GutendexBook {
    id: number;
    title: string;
    authors: { name: string; birth_year?: number; death_year?: number }[];
    translators: { name: string; birth_year?: number; death_year?: number }[];
    subjects: string[];
    bookshelves: string[];
    languages: string[];
    copyright: boolean | null;
    media_type: string;
    formats: Record<string, string>;
    download_count: number;
}

export interface GutendexResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: GutendexBook[];
}

export const GutendexService = {
    async search(query: string, lang: string = 'en', page: number = 1): Promise<GutendexResponse> {
        // Check cache
        const cached = await LibraryDB.getCache(query, lang, page);
        if (cached) {
            console.log("Loaded Gutendex cache");
            return cached;
        }

        // Build URL
        const url = new URL(BASE_URL);
        url.searchParams.set('search', query);
        // Gutendex supports 'languages' param? Docs say yes: /books?languages=en,fr
        // User requested: Language default "en" + optional toggle for "All languages"
        if (lang !== 'all') {
            url.searchParams.set('languages', lang);
        }
        if (page > 1) {
            url.searchParams.set('page', page.toString());
        }

        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`Gutendex API error: ${res.statusText}`);

        const data = await res.json();

        // Populate cache
        await LibraryDB.setCache(query, lang, page, data);

        return data;
    },

    // Helper to pick best format candidates
    getDownloadCandidates(book: GutendexBook): { mime: string, url: string }[] {
        const candidates: { mime: string, url: string }[] = [];
        const formats = book.formats;

        // Priority 1: Plain Text (most reliable)
        if (formats['text/plain; charset=utf-8']) {
            candidates.push({ mime: 'text/plain; charset=utf-8', url: formats['text/plain; charset=utf-8'] });
        }
        if (formats['text/plain']) {
            candidates.push({ mime: 'text/plain', url: formats['text/plain'] });
        }
        if (formats['text/plain; charset=us-ascii']) {
            candidates.push({ mime: 'text/plain; charset=us-ascii', url: formats['text/plain; charset=us-ascii'] });
        }

        // Priority 2: HTML (good structure)
        if (formats['text/html']) {
            candidates.push({ mime: 'text/html', url: formats['text/html'] });
        }

        // Priority 3: EPUB (Offline first logic will sort this to top if preferEpub is true)
        if (formats['application/epub+zip']) {
            candidates.push({ mime: 'application/epub+zip', url: formats['application/epub+zip'] });
        }

        // Priority 4: Hardcoded Fallbacks (Only if no valid formats found above, OR if they all fail - caller iterates)
        // We add them here so the caller has options if the primary links 404.
        const id = book.id;
        // https://www.gutenberg.org/files/{id}/{id}-0.txt
        candidates.push({ mime: 'fallback/files-0-txt', url: `https://www.gutenberg.org/files/${id}/${id}-0.txt` });
        // https://www.gutenberg.org/cache/epub/{id}/pg{id}.txt
        candidates.push({ mime: 'fallback/cache-txt', url: `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt` });

        return candidates;
    }
};
